import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";

const newRow = () => ({
  key: `${Date.now()}-${Math.random()}`,
  fee_category_id: "",
  fee_type_id: "",
  seattype: "",
  percent: "",
  fee_check: true,
});
const seatTypes = ["A", "B", "C", "M", "CONVENER", "MANAGEMENT", "ALL"];

export default function FeePercent() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = useMemo(() => JSON.parse(sessionStorage.getItem("user") || "{}"), []);
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const [batchId, setBatchId] = useState("");
  const [acyearId, setAcyearId] = useState("");
  const [batches, setBatches] = useState([]);
  const [years, setYears] = useState([]);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [entryRows, setEntryRows] = useState([newRow()]);
  const [savedRows, setSavedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [message, setMessage] = useState({ open: false, text: "", severity: "success" });
  const headers = { Authorization: `Bearer ${sessionStorage.getItem("token")}` };
  const notify = (text, severity = "success") => setMessage({ open: true, text, severity });

  const loadRules = async () => {
    const { data } = await axios.get(`${API_BASE_URL}/api/fee-percent/${clientId}/${collegeId}`, {
      headers,
    });
    setSavedRows((data?.data || []).map((row) => ({ ...row, id: String(row.fee_percent_id) })));
  };

  useEffect(() => {
    if (!clientId || !collegeId) return;
    const load = async () => {
      setLoading(true);
      const [batchResult, yearResult, categoryResult] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/api/admittedbatches/${clientId}/${collegeId}`, { headers }),
        axios.get(`${API_BASE_URL}/api/acyear/by-college/${clientId}/${collegeId}`, { headers }),
        axios.get(`${API_BASE_URL}/api/fee-categories/${clientId}/${collegeId}`, { headers }),
      ]);

      const batchPayload = batchResult.status === "fulfilled" ? batchResult.value.data : null;
      const batchRows = Array.isArray(batchPayload)
        ? batchPayload
        : batchPayload?.items || batchPayload?.data?.items || batchPayload?.data || [];
      setBatches(batchRows);
      if (batchRows.length)
        setBatchId((current) => current || String(batchRows[0].admittedbatch_id));

      const yearPayload = yearResult.status === "fulfilled" ? yearResult.value.data : null;
      const yearRows = Array.isArray(yearPayload)
        ? yearPayload
        : yearPayload?.items || yearPayload?.data?.items || yearPayload?.data || [];
      setYears(yearRows);
      if (yearRows.length) {
        const activeYear = yearRows.find((year) => Number(year.is_active) === 1) || yearRows[0];
        setAcyearId((current) => current || String(activeYear.acyear_id));
      }

      const categoryPayload =
        categoryResult.status === "fulfilled" ? categoryResult.value.data : null;
      const categoryRows = Array.isArray(categoryPayload)
        ? categoryPayload
        : categoryPayload?.items || categoryPayload?.data?.items || categoryPayload?.data || [];
      setCategories(categoryRows);

      const typeResponses = await Promise.allSettled(
        categoryRows.map((category) =>
          axios.get(
            `${API_BASE_URL}/api/fee-types/${clientId}/${collegeId}/${category.fee_category_id}`,
            { headers }
          )
        )
      );
      setTypes(
        typeResponses.flatMap((result) => {
          if (result.status !== "fulfilled") return [];
          const payload = result.value.data;
          return Array.isArray(payload)
            ? payload
            : payload?.items || payload?.data?.items || payload?.data || [];
        })
      );

      try {
        await loadRules();
      } catch (error) {
        notify(error.response?.data?.message || "Fee percentage table is not available", "warning");
      }
      if (batchResult.status === "rejected") notify("Unable to load admitted batches", "error");
      else if (!batchRows.length) notify("No admitted batches found for this college", "warning");
      if (yearResult.status === "rejected") notify("Unable to load academic years", "error");
      else if (!yearRows.length) notify("No academic years found for this college", "warning");
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, collegeId]);

  const changeRow = (key, field, value) =>
    setEntryRows((rows) =>
      rows.map((row) => {
        if (row.key !== key) return row;
        return {
          ...row,
          [field]: value,
          ...(field === "fee_category_id" ? { fee_type_id: "" } : {}),
        };
      })
    );

  const save = async () => {
    if (!batchId || !acyearId) return notify("Select admitted batch and academic year", "warning");
    if (
      entryRows.some(
        (row) => !row.fee_category_id || !row.fee_type_id || !row.seattype || row.percent === ""
      )
    ) {
      return notify("Complete all fields in every row", "warning");
    }
    setLoading(true);
    try {
      if (editingId) {
        await axios.put(
          `${API_BASE_URL}/api/fee-percent/${clientId}/${collegeId}/${editingId}`,
          {
            ...entryRows[0],
            modified_by: user?.user_email || "",
          },
          { headers }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/api/fee-percent/${clientId}/${collegeId}`,
          {
            admittedbatch_id: batchId,
            acyear_id: acyearId,
            rows: entryRows,
            created_by: user?.user_email || "",
          },
          { headers }
        );
      }
      notify(editingId ? "Fee percentage rule updated" : "Fee percentage rules saved");
      setEditingId("");
      setEntryRows([newRow()]);
      await loadRules();
    } catch (error) {
      notify(error.response?.data?.message || "Unable to save rules", "error");
    } finally {
      setLoading(false);
    }
  };

  const edit = (row) => {
    setBatchId(String(row.admittedbatch_id));
    setAcyearId(String(row.acyear_id));
    setEditingId(String(row.fee_percent_id));
    setEntryRows([
      {
        key: String(row.fee_percent_id),
        fee_category_id: String(row.fee_category_id),
        fee_type_id: String(row.fee_type_id),
        seattype: row.seattype,
        percent: Number(row.percent),
        fee_check: Boolean(row.fee_check),
      },
    ]);
  };

  const remove = async (row) => {
    const result = await Swal.fire({
      title: "Delete this fee percentage rule?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/fee-percent/${clientId}/${collegeId}/${row.fee_percent_id}`,
        { headers }
      );
      notify("Fee percentage rule deleted");
      await loadRules();
    } catch (error) {
      notify(error.response?.data?.message || "Unable to delete rule", "error");
    }
  };

  const label = (list, id, idKey, nameKey) =>
    list.find((item) => String(item[idKey]) === String(id))?.[nameKey] || "";
  const columns = [
    {
      field: "batch",
      headerName: "Admitted Batch",
      minWidth: 130,
      valueGetter: (p) =>
        label(batches, p.row.admittedbatch_id, "admittedbatch_id", "admittedbatch"),
    },
    {
      field: "year",
      headerName: "Academic Year",
      minWidth: 130,
      valueGetter: (p) => label(years, p.row.acyear_id, "acyear_id", "academic_year"),
    },
    {
      field: "category",
      headerName: "Fee Category",
      minWidth: 140,
      valueGetter: (p) =>
        label(categories, p.row.fee_category_id, "fee_category_id", "fee_category_name"),
    },
    {
      field: "type",
      headerName: "Fee Type",
      minWidth: 180,
      flex: 1,
      valueGetter: (p) => label(types, p.row.fee_type_id, "fee_type_id", "fee_type_name"),
    },
    { field: "seattype", headerName: "Seat Type", width: 120 },
    {
      field: "percent",
      headerName: "Percent",
      width: 100,
      valueGetter: (p) => Number(p.row.percent),
    },
    {
      field: "fee_check",
      headerName: "Check",
      width: 90,
      valueGetter: (p) => (p.row.fee_check ? "Yes" : "No"),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 110,
      sortable: false,
      renderCell: (p) => (
        <Stack direction="row">
          <Tooltip title="Edit">
            <IconButton onClick={() => edit(p.row)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton color="error" onClick={() => remove(p.row)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Fee Percentage Setup
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="medium"
              label="Admitted Batch"
              value={batchId}
              disabled={Boolean(editingId)}
              onChange={(e) => setBatchId(e.target.value)}
              SelectProps={{ native: true }}
              select
              InputLabelProps={{ shrink: true }}
            >
              <option value="">Select Admitted Batch</option>
              {batches.map((x) => (
                <option key={x.admittedbatch_id} value={x.admittedbatch_id}>
                  {x.admittedbatch}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="medium"
              label="Academic Year"
              value={acyearId}
              disabled={Boolean(editingId)}
              onChange={(e) => setAcyearId(e.target.value)}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="">Select Academic Year</option>
              {years.map((x) => (
                <option key={x.acyear_id} value={x.acyear_id}>
                  {x.academic_year}
                </option>
              ))}
            </TextField>
          </Grid>
        </Grid>
        {entryRows.map((row, index) => (
          <Grid container spacing={1.5} alignItems="center" sx={{ mb: 1 }} key={row.key}>
            <Grid item xs={12} md={2.2}>
              <TextField
                select
                fullWidth
                size="medium"
                label="Fee Category"
                value={row.fee_category_id}
                onChange={(e) => changeRow(row.key, "fee_category_id", e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">Select Fee Category</option>
                {categories.map((x) => (
                  <option key={x.fee_category_id} value={x.fee_category_id}>
                    {x.fee_category_name}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2.5}>
              <TextField
                select
                fullWidth
                size="medium"
                label="Fee Type"
                value={row.fee_type_id}
                onChange={(e) => changeRow(row.key, "fee_type_id", e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">Select Fee Type</option>
                {types
                  .filter((x) => String(x.fee_category_id) === String(row.fee_category_id))
                  .map((x) => (
                    <option key={x.fee_type_id} value={String(x.fee_type_id)}>
                      {x.fee_type_name}
                    </option>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={1.8}>
              <TextField
                select
                fullWidth
                size="medium"
                label="Seat Type"
                value={row.seattype}
                onChange={(e) => changeRow(row.key, "seattype", e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">Select Seat Type</option>
                {seatTypes.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <TextField
                fullWidth
                size="medium"
                type="number"
                label="Percent"
                value={row.percent}
                inputProps={{ min: 0, max: 100 }}
                onChange={(e) => changeRow(row.key, "percent", e.target.value)}
              />
            </Grid>
            <Grid item xs={8} md={1.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={row.fee_check}
                    onChange={(e) => changeRow(row.key, "fee_check", e.target.checked)}
                  />
                }
                label="Check"
              />
            </Grid>
            <Grid item xs={4} md={1}>
              <IconButton
                color="error"
                disabled={entryRows.length === 1 || Boolean(editingId)}
                onClick={() => setEntryRows((rows) => rows.filter((x) => x.key !== row.key))}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            startIcon={<SaveIcon />}
            variant="contained"
            disabled={loading}
            onClick={save}
            sx={{
              bgcolor: "#1976d2",
              color: "#fff !important",
              fontWeight: 700,
              minWidth: 110,
              "& .MuiButton-startIcon": { color: "#fff" },
              "&:hover": { bgcolor: "#125aa3" },
              "&.Mui-disabled": {
                bgcolor: "#90caf9",
                color: "#fff !important",
                opacity: 0.8,
              },
            }}
          >
            {editingId ? "Update" : "Save"}
          </Button>
          {editingId && (
            <Button
              onClick={() => {
                setEditingId("");
                setEntryRows([newRow()]);
              }}
            >
              Cancel
            </Button>
          )}
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ height: 430 }}>
        <DataGrid
          rows={savedRows}
          columns={columns}
          loading={loading}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
        />
      </Paper>
      <Snackbar
        open={message.open}
        autoHideDuration={5000}
        onClose={() => setMessage((x) => ({ ...x, open: false }))}
      >
        <Alert severity={message.severity}>{message.text}</Alert>
      </Snackbar>
    </Box>
  );
}
